/**
 * Where a visitor came from, for Lab43 HQ's "where sign-ups came from" (lab43-auth docs/API.md → Stats for HQ,
 * `sources`): mc-planner.com's links to the app carry `?via=<host>`, the site that sent the visitor to mc-planner.com
 * (a host only, such as reddit.com; no cookie). The app keeps it for this tab only (sessionStorage), takes it out of the
 * address bar at once, and sends it as `lab43_via` with the sign-in code request that makes an account (lib/auth.ts).
 * The account site counts the account under it when the account is confirmed and then removes it from the account (after
 * a day if it's never confirmed): it's kept only as a count. Plain functions, tested in apps/web/scripts/via.test.ts.
 */

const KEY = "mcp-via";
const HOST = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const OWN = new Set(["mc-planner.com", "app.mc-planner.com"]);

/** A host as counted: lower case, without a scheme, www., port or path; null when it isn't a host name (or an IP). */
export function viaHost(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const h = value
    .trim()
    .toLowerCase()
    .replace(/^([a-z][a-z0-9+.-]*:\/\/)?(www\.)?/, "")
    .replace(/[:/?#].*$/, "");
  return h.length <= 100 && HOST.test(h) && /\.[a-z][a-z0-9-]*$/.test(h) && !OWN.has(h) ? h : null;
}

type Place = { href: string };
type Store = Pick<Storage, "getItem" | "setItem">;

/**
 * Reads `?via=` from the address the app opened at: kept for this tab (when it's a host), and the address bar loses it
 * either way. Returns the address to show instead, or null when there was nothing to take out.
 */
export function takeVia(place: Place, store: Store): string | null {
  const url = new URL(place.href);
  if (!url.searchParams.has("via")) return null;
  const host = viaHost(url.searchParams.get("via"));
  try {
    if (host) store.setItem(KEY, host);
  } catch {
    // storage blocked: the sign-up just counts as not known
  }
  url.searchParams.delete("via");
  return url.pathname + (url.search === "?" ? "" : url.search) + url.hash;
}

/** At start: keep `?via=` for this tab and tidy the address bar. Never throws. */
export function captureVia() {
  try {
    const clean = takeVia(window.location, window.sessionStorage);
    if (clean !== null) window.history.replaceState(window.history.state, "", clean);
  } catch {
    // no window or storage (tests, a locked-down browser): nothing to keep
  }
}

/** The site that sent this tab's visitor, when known. */
export function currentVia(store?: Store): string | null {
  try {
    return viaHost((store ?? window.sessionStorage).getItem(KEY));
  } catch {
    return null;
  }
}
